import { HttpClientSilent } from 'app/services/http/httpClient';

/**
 * NSW Point address API client.
 *
 * Routes address suggestion and detail requests through the OBS backend,
 * which calls the NSW Point V3 API and returns responses in the V2 shape
 * the GEL AutoSuggestAddress component expects.
 *
 * These functions are passed to the AutoSuggestAddress `backendAPI` prop so
 * the component no longer calls NSW Point directly from the browser.
 */

interface Suggestion {
  address: string;
  id: string;
  rank?: number;
}

interface SuggestionsResult {
  suggestions: Suggestion[];
  requestNo: number;
}

/**
 * Fetch address suggestions from the OBS backend.
 *
 * The GEL component's internal `useNSWPointV2API` hook calls this with:
 *   (value, requestNo, url, params)
 *
 * We only need `value` and `requestNo` — the `url` and `params` are the
 * would-be NSW Point request that we're replacing.
 *
 * Must return `{ suggestions: [{address, id, rank}, ...], requestNo }`.
 */
export const getSuggestions = async (value: string, requestNo: number): Promise<SuggestionsResult> => {
  const defaultResult: SuggestionsResult = { suggestions: [], requestNo };

  try {
    const response = await HttpClientSilent.get('/NSWPoint/getSuggestions', {
      params: {
        queryString: value,
        type: 'all',
      },
    });

    // The backend NSWPointController returns a JSON array of suggestions
    // in V2 shape: [{address, id, rank}, ...]
    const suggestions = Array.isArray(response) ? response : [];
    return { suggestions, requestNo };
  } catch (err) {
    console.error('NSW Point getSuggestions error:', err);
    return defaultResult;
  }
};

/**
 * Fetch address details by NSW Point ID from the OBS backend.
 *
 * The GEL component's internal `useNSWPointV2API` hook calls this with:
 *   (id, url, body)
 *
 * We only need `id`. The `url` and `body` are the would-be NSW Point
 * request that we're replacing.
 *
 * Must return the V2-shaped response:
 *   { data: { geo: {...}, addressDetails: {...}, addressId, localGovernmentArea, ... } }
 *
 * The GEL component then runs its own `convertAddress()` to extract
 * flat address fields for the form.
 */
export const getDetailsById = async (id: string): Promise<Record<string, any>> => {
  try {
    const response = await HttpClientSilent.get('/NSWPoint/getDetails', {
      params: {
        id,
        outFields: 'lgaPid,lgaName,lgaShortName',
      },
    });

    // The backend NSWPointController returns V2-shaped JSON:
    // { data: { geo, addressDetails, addressId, localGovernmentArea, ... } }
    return response ?? {};
  } catch (err) {
    console.error('NSW Point getDetailsById error:', err);
    return {};
  }
};

/**
 * Pre-built backendAPI object ready to pass to the AutoSuggestAddress
 * `backendAPI` prop.
 *
 * Usage:
 *   <AutoSuggestAddress
 *     apiOptions={{ stateTerritory: ['NSW'] }}
 *     backendAPI={nswPointBackendAPI}
 *     ...
 *   />
 *
 * Note: `apiOptions.key` must be omitted (or set to null/undefined) so the
 * GEL component delegates to `backendAPI` instead of calling NSW Point
 * directly from the browser.
 */
export const nswPointBackendAPI = {
  getSuggestions,
  getDetailsById,
};
